'use client'

import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  Popover,
  PopoverButton,
  PopoverPanel,
} from '@headlessui/react'
import clsx from 'clsx'
import {
  ArrowRight,
  ChevronDown,
  Menu,
  SquareArrowOutUpRight,
  X,
} from 'lucide-react'
import { Fragment, useState } from 'react'
import { FlyoutLink, Link, SimpleLink } from './link'
import { Logo } from './logo'

const links: SimpleLink[] | FlyoutLink[] = [
  { href: '/', label: 'Home', external: false } as SimpleLink,
  { href: '/our-mission', label: 'Our Mission', external: false } as SimpleLink,
  {
    href: '/tools',
    label: 'Tools',
    external: false,
    primary: true,
  } as SimpleLink,
]

function DesktopFlyout({ link }: { link: FlyoutLink }) {
  return (
    <Popover className="group/item relative flex">
      {({ open }) => (
        <>
          <PopoverButton
            className={clsx(
              'data-hover:bg-black/[2.5%] flex items-center px-4 py-3 text-base font-medium text-gray-950 bg-blend-multiply focus:bg-black/[2.5%] focus:outline-none',
              open && 'text-secondary-500 transition delay-75',
            )}
          >
            <span
            // className={clsx(open && 'text-secondary-500 transition delay-75')}
            >
              {link.label}
            </span>
            <ChevronDown
              aria-hidden="true"
              className={clsx(
                'ml-1 size-4 transition delay-75',
                open && '-rotate-180',
              )}
            />
          </PopoverButton>

          <PopoverPanel
            transition
            className="data-closed:translate-y-1 data-closed:opacity-0 data-enter:duration-200 data-enter:ease-out data-leave:duration-150 data-leave:ease-in absolute left-1/2 top-12 z-50 mt-5 flex w-screen max-w-max -translate-x-1/2 px-4 transition"
          >
            <div className="w-screen max-w-md flex-auto overflow-hidden rounded-3xl bg-white text-sm/6 shadow-lg ring-1 ring-gray-900/5">
              <div className="p-4">
                {link.flyout.map((item) => (
                  <div
                    key={item.label}
                    className="group relative flex gap-x-6 rounded-lg p-4 hover:bg-gray-50"
                  >
                    <div className="mt-1 flex size-11 flex-none items-center justify-center rounded-lg bg-gray-50 group-hover:bg-white">
                      <div className="text-lg font-semibold text-gray-900">
                        {item.label.charAt(0)}
                      </div>
                    </div>
                    <div>
                      <Link
                        href={item.href}
                        className="font-semibold text-gray-900"
                      >
                        <div className="relative flex items-center">
                          {item.label}
                          {item.external && (
                            <SquareArrowOutUpRight
                              className="ml-1 size-4 text-gray-950 transition-colors duration-200 ease-in-out group-hover:text-gray-900"
                              aria-hidden="true"
                            />
                          )}
                          {item.flag && (
                            <span className="ml-4 rounded-full bg-zinc-100 px-2.5 py-1.5 text-xs font-semibold text-zinc-400/70">
                              {item.flag}
                            </span>
                          )}
                        </div>
                        <span className="absolute inset-0" />
                      </Link>
                      <p className="mt-1 text-gray-600">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              {/* <div className="flex bg-gray-50"></div> */}
            </div>
          </PopoverPanel>
        </>
      )}
    </Popover>
  )
}

function DesktopItem({ link }: { link: SimpleLink }) {
  return (
    <div className="flex">
      <Link
        href={link.href}
        target={link.external ? '_blank' : undefined}
        className={clsx(
          link.primary
            ? 'bg-primary-600 hover:bg-primary-500 focus:bg-primary-500 px-4 py-1.5 text-white transition-all duration-200 hover:py-3 focus:py-3'
            : 'data-hover:bg-black/[2.5%] px-4 py-3 text-gray-950 transition-all duration-200 focus:bg-black/[2.5%]',
          'relative flex items-center rounded-lg text-base font-medium bg-blend-multiply focus:outline-none',
        )}
      >
        {link.label}
        {link.external && (
          <SquareArrowOutUpRight
            className="ml-1 size-4 text-gray-950 transition-colors duration-200 ease-in-out group-hover:text-gray-900"
            aria-hidden="true"
          />
        )}
      </Link>
    </div>
  )
}

function DesktopNav({ banner }: { banner?: React.ReactNode }) {
  return (
    <nav className="relative hidden items-center justify-between py-4 lg:flex">
      <Link
        href="/"
        className="flex items-center space-x-3 bg-blend-multiply focus:outline-none"
      >
        <Logo className="text-primary-600 h-10" />
      </Link>
      {banner && (
        <div className="relative hidden items-center py-3 lg:flex">
          {banner}
        </div>
      )}
      {/* <Link href="/" className="flex items-center space-x-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600">
          <span className="text-xl font-bold text-white">H</span>
        </div>
        <div>
          <div className="text-2xl font-bold text-gray-900">HelpU</div>
          <p className="text-sm text-gray-600">helpu.finance</p>
        </div>
      </Link> */}
      {/* <Link href="/">HelpU</Link> */}
      <div className="flex items-center gap-x-4">
        {links.map((item: SimpleLink | FlyoutLink) => (
          <Fragment key={item.label}>
            {item.hasOwnProperty('flyout') ? (
              <DesktopFlyout link={item as FlyoutLink} />
            ) : (
              <DesktopItem link={item as SimpleLink} />
            )}
          </Fragment>
        ))}
      </div>
    </nav>
  )
}

function MobileNavItem({ item }: { item: SimpleLink | FlyoutLink }) {
  return (
    <div key={item.label} className="py-2">
      {item.hasOwnProperty('flyout') ? (
        <div className="flex items-center px-4 py-3 text-base font-medium text-gray-950/70">
          {item.label}
        </div>
      ) : (
        <Link
          href={item.href}
          target={item.external ? '_blank' : undefined}
          className="data-hover:bg-black/[2.5%] flex items-center px-4 py-3 text-base font-medium text-gray-950 bg-blend-multiply focus:bg-black/[2.5%] focus:outline-none"
        >
          {item.label}
          {item.external && (
            <SquareArrowOutUpRight
              className="ml-1 size-4 text-gray-950 transition-colors duration-200 ease-in-out group-hover:text-gray-900"
              aria-hidden="true"
            />
          )}
        </Link>
      )}
      {item.hasOwnProperty('flyout') && (
        <div className="ml-8 mt-2 flex flex-col gap-2">
          {(item as FlyoutLink).flyout.map((subItem) => {
            return (
              <div key={subItem.label} className="flex items-center">
                <ArrowRight className="size-6" />
                <Link
                  key={subItem.label}
                  href={subItem.href}
                  target={subItem.external ? '_blank' : undefined}
                  className="data-hover:bg-black/[2.5%] flex items-center px-4 py-3 text-base font-medium text-gray-950 bg-blend-multiply focus:bg-black/[2.5%] focus:outline-none"
                >
                  {subItem.label}
                  {subItem.external && (
                    <SquareArrowOutUpRight
                      className="ml-1 size-4 text-gray-950 transition-colors duration-200 ease-in-out group-hover:text-gray-900"
                      aria-hidden="true"
                    />
                  )}
                </Link>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function MobileNav() {
  const [open, setOpen] = useState(false)

  return (
    <div className="flex items-center justify-between px-4 py-3 md:px-16 lg:hidden">
      <Link
        href="/"
        className="flex items-center space-x-3 bg-blend-multiply focus:outline-none"
      >
        <Logo className="text-primary-600 h-10" />
      </Link>

      <button
        onClick={() => setOpen(!open)}
        type="button"
        className="data-hover:bg-black/5 flex size-12 items-center justify-center self-center rounded-lg"
        aria-label="Open main menu"
      >
        <Menu className="size-6" />
      </button>

      <Dialog open={open} onClose={setOpen} className="relative z-10 lg:hidden">
        <DialogBackdrop
          transition
          className="data-closed:opacity-0 fixed inset-0 bg-gray-500/75 transition-opacity duration-500 ease-in-out"
        />

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
              <DialogPanel
                transition
                className="data-closed:translate-x-full pointer-events-auto w-screen max-w-md transform transition duration-500 ease-in-out sm:duration-700"
              >
                <div className="flex h-full flex-col overflow-y-scroll bg-white py-6 shadow-xl">
                  <div className="px-4 sm:px-6">
                    <div className="flex items-start justify-between">
                      <span></span>
                      {/* <DialogTitle className="text-base font-semibold text-gray-900">
                        Main menu
                      </DialogTitle> */}
                      <div className="ml-3 flex h-7 items-center">
                        <button
                          type="button"
                          onClick={() => setOpen(false)}
                          className="focus:ring-secondary-500 focus:outline-hidden relative rounded-md bg-white text-gray-400 hover:text-gray-500 focus:ring-2 focus:ring-offset-2"
                        >
                          <span className="absolute -inset-2.5" />
                          <span className="sr-only">Close menu</span>
                          <X aria-hidden="true" className="size-8" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="relative mt-6 flex-1 px-4 sm:px-6">
                    {links.map((item) => {
                      return MobileNavItem({ item } as {
                        item: SimpleLink | FlyoutLink
                      })
                    })}
                  </div>
                </div>
              </DialogPanel>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  )
}

export function Navbar({ banner }: { banner?: React.ReactNode }) {
  return (
    <header className="mx-auto max-w-7xl px-0 lg:px-16 xl:px-8">
      <DesktopNav banner={banner} />
      <MobileNav />
    </header>
  )
}
